import { useCallback } from 'react';
import {
  AddOnOptionSelections,
  Product,
  ProductProperty,
  UserSelection,
} from '@la/types';
import { getCaseInsensitiveStringMatch } from '@la/utilities';
import {
  getAddOnProducts,
  getRegistrationType,
  getTaxRate,
  getUserSelections,
  selectSelectionsWithErrors,
} from 'redux/selectors/registrationAddOnsSelectors';
import { calculatePrice } from 'domains/MemberRegistration/AddOnCard/AddOnPriceCalculator';
import { ProductWithMappedSelections } from 'domains/MemberRegistration/MemberRegistration.types';
import {
  setRegistrationAddOns as setRegistrationAddOnsAction,
  setRegistrationType as setRegistrationTypeAction,
  setSelectionsWithErrors as setSelectionsWithErrorsAction,
  setTaxRate as setTaxRateAction,
  setUserSelections as setUserSelectionsAction,
  updateOptionalAddOns as updateOptionalAddOnsAction,
  updateRequiredAddOnSelection as updateRequiredAddOnSelectionAction,
} from '../registrationAddOnsSlice';
import { useAppDispatch, useAppSelector } from '../store';

export const useRegistrationAddOns = () => {
  const dispatch = useAppDispatch();
  const taxRate = useAppSelector(getTaxRate);

  const addOnProducts: Product[] = useAppSelector(getAddOnProducts);
  const registrationType: 'player' | 'staff' | undefined =
    useAppSelector(getRegistrationType);
  const userSelections: AddOnOptionSelections[] =
    useAppSelector(getUserSelections);
  const selectionsWithErrors = useAppSelector(selectSelectionsWithErrors);

  const getAddOnProductById = useCallback(
    (id: number) => {
      return addOnProducts.find((addOn) => addOn.id === id);
    },
    [addOnProducts]
  );

  const getAddOnsByType = useCallback(
    (type: 'player' | 'staff') => {
      return addOnProducts.filter((addOn) =>
        addOn.assignedRoles.some((role) => role.role === type.toUpperCase())
      );
    },
    [addOnProducts]
  );

  const getAddOns = useCallback(
    (category: 'optional' | 'required', type: 'player' | 'staff') => {
      const addOnsByType = getAddOnsByType(type);

      return addOnsByType.filter((addOn) =>
        addOn.assignedRoles.some(
          (role) =>
            (category === 'optional' &&
              !role.required &&
              getCaseInsensitiveStringMatch(role.role, type)) ||
            (category === 'required' &&
              role.required &&
              getCaseInsensitiveStringMatch(role.role, type))
        )
      );
    },
    [getAddOnsByType]
  );

  const getOptionalPlayerAddOns = () => getAddOns('optional', 'player');
  const getOptionalStaffAddOns = () => getAddOns('optional', 'staff');
  const getRequiredPlayerAddOns = () => getAddOns('required', 'player');
  const getRequiredStaffAddOns = () => getAddOns('required', 'staff');

  const getRequiredAddOnsByType = useCallback(
    (type: 'player' | 'staff') => {
      const addOnsByType = getAddOnsByType(type);
      return addOnsByType.filter((addOn) =>
        addOn.assignedRoles.some(
          (role) => role.required && role.role === type.toUpperCase()
        )
      );
    },
    [getAddOnsByType]
  );

  const getOptionalAddOnsByType = (type: 'player' | 'staff') => {
    const addOnsByType = getAddOnsByType(type);
    return addOnsByType.filter((addOn) =>
      addOn.assignedRoles.some(
        (role) => !role.required && role.role === type.toUpperCase()
      )
    );
  };

  const getUserSelectionsByAddOnId = useCallback(
    (productId: number) => {
      const filteredUserSelections = userSelections.find(
        (selection) => selection.addOnId === productId
      );
      return filteredUserSelections ? filteredUserSelections.selections : [];
    },
    [userSelections]
  );

  /**
   * @param productId The ID of the product to get the selections for
   * @param userSelections An array of objects containing key-value pairs for each option's id and it's selected value
   * @returns A string representing the values of the selections
   */
  const getUserSelectionValuesByProduct = useCallback(
    (productId: number, userSelections: UserSelection[]) => {
      const product: Product | undefined = getAddOnProductById(productId);
      if (!product) {
        return '';
      }

      const displayNames: string[] = [];

      // Create a map for quick lookup of selected options by property ID
      const selectedOptionsMap = new Map(
        userSelections.map(({ optionId, selectedValue }) => [
          optionId,
          selectedValue,
        ])
      );

      // Sort properties by propertyOrder and process them in that order
      const sortedProperties: ProductProperty[] = [
        ...(product.properties ?? []),
      ].sort((a, b) => a.propertyOrder - b.propertyOrder);

      for (const property of sortedProperties) {
        const selectedValue = selectedOptionsMap.get(property.id.toString());

        if (selectedValue !== undefined) {
          if (property.inputControlType === 'PICK_LIST') {
            // For pick lists, find the matching value object and use its name
            const valueObj = property.values?.find(
              (val: any) => val.value === selectedValue
            );

            if (valueObj) {
              displayNames.push(valueObj.name);
            }
          } else if (
            property.inputControlType === 'NUMERIC' ||
            property.inputControlType === 'TEXT_AREA' ||
            property.inputControlType === 'TEXT_BOX'
          ) {
            // For text inputs, use the value directly
            displayNames.push(selectedValue);
          }
        }
      }

      return displayNames.join(', ');
    },
    [getAddOnProductById]
  );

  const setRegistrationAddOns = useCallback(
    (addOns: Product[]) => {
      dispatch(setRegistrationAddOnsAction(addOns));
    },
    [dispatch]
  );

  const setRegistrationType = useCallback(
    (registrationType: string) => {
      const regType = registrationType.toLowerCase().includes('player')
        ? 'player'
        : 'staff';
      dispatch(setRegistrationTypeAction(regType));
    },
    [dispatch]
  );

  const setTaxRate = useCallback(
    (taxRate: number) => {
      dispatch(setTaxRateAction(taxRate));
    },
    [dispatch]
  );

  const setUserSelections = useCallback(
    (selections: AddOnOptionSelections[]) => {
      dispatch(setUserSelectionsAction(selections));
    },
    [dispatch]
  );

  const updateAddOnQuantity = useCallback(
    (productInstanceId: string, quantity: number) => {
      const updatedSelections = userSelections.map((userSelection) =>
        userSelection.productInstanceId === productInstanceId
          ? { ...userSelection, quantity }
          : userSelection
      );
      dispatch(setUserSelectionsAction(updatedSelections));
    },
    [dispatch, userSelections]
  );

  const updateRequiredAddOnSelection = useCallback(
    (addOnId: number, propertyId: string, value: string) => {
      dispatch(
        updateRequiredAddOnSelectionAction({ addOnId, propertyId, value })
      );
    },
    [dispatch]
  );

  /* Functions used by getSelectedProducts() */
  const calculateProductPrice = useCallback(
    (product: Product, selections: UserSelection[]): number => {
      let calculatedProductPrice = product.price;

      if (
        product.pricingConfigurations &&
        product.pricingConfigurations.length > 0
      ) {
        calculatedProductPrice = calculatePrice(
          product.price,
          selections,
          product.pricingConfigurations
        );
      }

      return calculatedProductPrice;
    },
    []
  );

  const mapSelectionToProduct = useCallback(
    (selection: AddOnOptionSelections, products: Product[]) => {
      const product = products.find(
        (product) => product.id === selection.addOnId
      );

      if (!product) {
        return null;
      }

      const price = calculateProductPrice(product, selection.selections);
      const subTotal = price * selection.quantity;
      const salesTax =
        product.salesTaxRequired && taxRate
          ? Math.round(subTotal * taxRate) / 100
          : 0;
      const itemCost = subTotal + salesTax;

      return {
        addOnType: selection.addOnType,
        itemCost: itemCost,
        productId: product.id,
        productInstanceId: selection.productInstanceId,
        productName: product.name,
        quantity: selection.quantity,
        salesTax: salesTax,
        salesTaxRequired: product.salesTaxRequired,
        selectionTypes: selection.selections,
        subTotal: subTotal,
        taxRate: taxRate,
      };
    },
    [calculateProductPrice, taxRate]
  );
  /* */

  const getSelectedProduct = useCallback(
    (productId: number) => {
      const selection = userSelections.find(
        (selection) => selection.addOnId === productId && selection.quantity > 0
      );

      if (!selection) {
        return null;
      }

      return mapSelectionToProduct(selection, addOnProducts);
    },
    [addOnProducts, mapSelectionToProduct, userSelections]
  );

  const getSelectedProducts = useCallback<
    () => ProductWithMappedSelections[]
  >(() => {
    return (
      userSelections
        .filter((selection) => selection.quantity > 0)
        .map((selection) => mapSelectionToProduct(selection, addOnProducts))
        // The "item is NonNullable<typeof item>" assertion lets TS know that null values are being removed.
        .filter((item): item is NonNullable<typeof item> => item !== null)
    );
  }, [addOnProducts, mapSelectionToProduct, userSelections]);

  const getTotalProductCost = useCallback(() => {
    const selectedProducts = getSelectedProducts();
    const total = selectedProducts.reduce((sum, product) => {
      return sum + product.itemCost;
    }, 0);
    return Math.round(total * 100) / 100;
  }, [getSelectedProducts]);

  const updateOptionalAddOns = (optionalAddOn: AddOnOptionSelections) => {
    const { productInstanceId, quantity } = optionalAddOn;
    const existingAddOn = userSelections.find(
      (selection) => selection.productInstanceId === productInstanceId
    );
    let updatedUserSelections;

    if (existingAddOn) {
      updatedUserSelections = [
        ...userSelections.filter(
          (selection) => selection.productInstanceId !== productInstanceId
        ),
        { ...existingAddOn, quantity: existingAddOn.quantity + quantity },
      ];
    } else {
      updatedUserSelections = [...userSelections, optionalAddOn];
    }

    dispatch(updateOptionalAddOnsAction({ updatedUserSelections }));
  };

  const getSelectionsWithErrors = (): string[] => {
    let newErrors: string[] = [];
    const requiredAddOns = getRequiredAddOnsByType(registrationType!);
    // for each required add on in addOnProducts
    for (const requiredAddOn of requiredAddOns) {
      // locate the properties that are required
      for (const property of requiredAddOn.properties ?? []) {
        if (property.required) {
          const matchingSelectionsRecord = userSelections.find(
            (userSelection) => userSelection.addOnId === requiredAddOn.id
          );
          const propertyIdString = property.id.toString();
          const matchingSelection = matchingSelectionsRecord?.selections.find(
            (selection: UserSelection) =>
              selection.optionId === propertyIdString
          );

          if (!matchingSelection) {
            // Add error if matchingSelection doesn't exist
            newErrors.push(propertyIdString);
          }
        }
      }
    }
    return newErrors;
  };

  const validateRequiredAddOns = () => {
    const newErrors = getSelectionsWithErrors();
    dispatch(setSelectionsWithErrorsAction(newErrors));
    return newErrors.length === 0;
  };

  const deleteOptionalAddOn = (productInstanceId: string) => {
    const updatedUserSelections = userSelections.filter(
      (selection) => selection.productInstanceId !== productInstanceId
    );
    dispatch(updateOptionalAddOnsAction({ updatedUserSelections }));
  };

  const removeSelectionWithError = (propertyId: string) => {
    const newErrors = selectionsWithErrors.filter((err) => err !== propertyId);
    dispatch(setSelectionsWithErrorsAction(newErrors));
  };

  return {
    deleteOptionalAddOn,
    getAddOnsByType,
    getAddOnProductById,
    getOptionalAddOnsByType,
    getRequiredAddOnsByType,
    getRequiredStaffAddOns,
    getOptionalStaffAddOns,
    getRequiredPlayerAddOns,
    getOptionalPlayerAddOns,
    getUserSelectionsByAddOnId,
    getUserSelectionValuesByProduct,
    getSelectedProduct,
    getSelectedProducts,
    getTotalProductCost,
    removeSelectionWithError,
    setRegistrationAddOns,
    setRegistrationType,
    setTaxRate,
    setUserSelections,
    updateAddOnQuantity,
    updateRequiredAddOnSelection,
    updateOptionalAddOns,
    validateRequiredAddOns,
  };
};
